import type {SplitName} from '@/config';

import {useUpliftAds as useUpliftAdsUI} from '@travel-frontend/ui-components';
import {format} from 'date-fns';
import {useEffect, useMemo} from 'react';

import {PAGE_OFFERS} from '@/consts';
import {useSeaCampaign} from '@/modules/common/context/SeaCampaign';
import {useCurrentPage} from '@/modules/common/hooks/useCurrentPage';
import {useLocalSplit} from '@/modules/common/hooks/useLocalSplit';
import {useSearchParams} from '@/modules/common/hooks/useSearchParams';

export type UseUpliftAdsReturn = {
  isLoading: boolean;
  advertisement?: ReturnType<
    typeof useUpliftAdsUI
  >['advertisements'] extends Map<number, infer I>
    ? I
    : never;
  shouldShowBanner: boolean;
};

const mapZoneIdToSplit = (
  zoneId: number,
  isSea: boolean,
  isOffersPage: boolean
) => {
  switch (zoneId) {
    case import.meta.env.VITE_UPLIFT_ADS_NATIVE_TOP_ZONE_ID: {
      return isSea
        ? 'FPO_5979_find_platform_195_SEA_Hotellist_Top'
        : 'FPO_5918_find_platform_195';
    }
    case import.meta.env.VITE_UPLIFT_ADS_FIRST_INLINE_ZONE_ID:
    case import.meta.env.VITE_UPLIFT_ADS_SECOND_INLINE_ZONE_ID: {
      return isSea
        ? 'FPO_5970_find_inline_platform_195_SEA'
        : 'FPO_5968_find_inline_platform_195';
    }
    case import.meta.env.VITE_UPLIFT_ADS_FIRST_SKYSCRAPER_ZONE_ID:
    case import.meta.env.VITE_UPLIFT_ADS_SECOND_SKYSCRAPER_ZONE_ID: {
      if (isOffersPage) {
        return isSea
          ? 'FPO_6208_find_skyscraper_offerlist_platform_195_SEA'
          : 'FPO_5872_find_skyscraper_offerlist_platform_195';
      } else {
        return isSea
          ? 'FPO_6207_find_skyscraper_hotellist_platform_195_SEA'
          : 'FPO_5871_find_skyscraper_hotellist_platform_195';
      }
    }
    default: {
      return '' as SplitName;
    }
  }
};

export const useUpliftAds = (zoneId: number): UseUpliftAdsReturn => {
  const [searchParams] = useSearchParams();
  const {active} = useSeaCampaign();
  const currentPage = useCurrentPage();
  const upliftAdsSplitEnabled =
    useLocalSplit(mapZoneIdToSplit(zoneId, active, currentPage === PAGE_OFFERS))
      .treatment === 'on';
  const {getAdvertisements, advertisements, isLoading} = useUpliftAdsUI();

  useEffect(() => {
    if (!upliftAdsSplitEnabled) {
      return;
    }

    getAdvertisements({
      zoneIds: [zoneId],
      keywords: searchParams?.destinationId ? [searchParams.destinationId] : [],
      customTargeting: {
        DestOrHotel: searchParams?.destinationId || '',
        DepartAirport: searchParams?.airports?.toString() || '',
        DepartMonthYear: searchParams?.dateMin
          ? format(new Date(searchParams?.dateMin), 'MMyyyy')
          : '',
        Duration: searchParams?.durationMin || 0,
        Adults: searchParams?.adults || 0,
        Children: searchParams?.rooms?.childrenCount || 0,
      },
    });
  }, [getAdvertisements, searchParams, upliftAdsSplitEnabled, zoneId]);

  return useMemo(
    () => ({
      isLoading,
      advertisement: advertisements.get(zoneId),
      shouldShowBanner: advertisements.has(zoneId),
    }),
    [isLoading, advertisements, zoneId]
  );
};
